import classNames from "classnames";
import { motion } from "motion/react";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { branchEmployees, hasRealContent, OrgTree, unitHeight } from "../layout";
import { UnitPatch } from "../org-tree-store";
import { OrgEmployeeData, OrgPositionData, OrgStaffSlot, OrgUnitTypeData } from "../types";
import { PositionsDictionaryDialog } from "../../components/PositionsDictionaryDialog";
import { sameStaff, StaffEditor, StaffRow, toRows, toStaff } from "./StaffEditor";
import { describeUnit } from "./nodes";
import { AreaField, Field, SelectField, TextField } from "./fields";
import { CloseIcon, UnitTypeIcon } from "./icons";
import styles from "./OrgTree.module.scss";

interface UnitPanelProps {
    tree: OrgTree;
    unitId: string;
    types: OrgUnitTypeData[];
    positions: OrgPositionData[];
    /** все сотрудники: занятость ставок не зависит от фильтров дерева */
    employees: OrgEmployeeData[];
    edit: boolean;
    onClose(): void;
    onSave(patch: UnitPatch): Promise<unknown>;
    onSaveStaff(unitId: string, staff: OrgStaffSlot[]): Promise<unknown>;
    /** право смотреть справочник должностей — кнопки «+» и «открыть», как в карточке сотрудника */
    canOpenPositions: boolean;
    /** должности поменяли в справочнике — перечитать дерево */
    onPositionsChanged(): void;
    onDelete(id: string): Promise<unknown>;
    /** есть несохранённые правки: закрыть карточку можно только с подтверждением */
    onDirtyChange(dirty: boolean): void;
}

/**
 * Карточка подразделения справа от дерева: порядковый №, наименование, тип,
 * руководитель (только сотрудники этого узла и вложенных), примечания, штатное расписание.
 */
export const UnitPanel = ({
    tree,
    unitId,
    types,
    positions,
    employees,
    edit,
    onClose,
    onSave,
    onSaveStaff,
    canOpenPositions,
    onPositionsChanged,
    onDelete,
    onDirtyChange,
}: UnitPanelProps) => {
    const item = tree.byId.get(unitId);
    const unit = item?.unit;

    const [name, setName] = useState("");
    const [order, setOrder] = useState("");
    const [typeId, setTypeId] = useState("");
    const [managerId, setManagerId] = useState("");
    const [description, setDescription] = useState("");
    const [staffRows, setStaffRows] = useState<StaffRow[]>([]);
    const [dictionaryOpen, setDictionaryOpen] = useState(false);
    // новая строка штата, к которой надо прокрутить список
    const [scrollTo, setScrollTo] = useState<string | null>(null);
    const staffScrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!scrollTo) {
            return;
        }
        staffScrollRef.current
            ?.querySelector<HTMLElement>(`[data-staff-key="${scrollTo}"]`)
            ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        setScrollTo(null);
    }, [scrollTo]);

    const addVacancy = () => {
        const key = `new-${Date.now()}`;
        setStaffRows((rows) => [...rows, { key, positionId: "", gradeGuid: "", count: "1" }]);
        setScrollTo(key);
    };
    const [busy, setBusy] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (unit) {
            setName(unit.name);
            setOrder(unit.order != null ? String(unit.order) : "");
            setTypeId(unit.unitTypeId ?? "");
            setManagerId(unit.managerId ?? "");
            setDescription(unit.description ?? "");
            setConfirmDelete(false);
        }
        // карточку переинициализируем только при смене узла и после сохранения
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unit?.id, unit?.name, unit?.order, unit?.unitTypeId, unit?.managerId, unit?.description]);

    // по содержимому, а не по ссылке: тихое перечитывание дерева не должно сбрасывать набранное
    const staffKey = JSON.stringify(unit?.staff ?? []);
    useEffect(() => {
        if (unit) {
            setStaffRows(toRows(unit.staff ?? []));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unit?.id, staffKey]);

    const occupied = useMemo(() => {
        const result = new Map<string, number>();
        employees.forEach((e) => {
            if (e.departmentId === unitId && !e.fired && e.positionId) {
                result.set(e.positionId, (result.get(e.positionId) ?? 0) + 1);
            }
        });
        return result;
    }, [employees, unitId]);
    const positionsById = useMemo(() => new Map(positions.map((p) => [p.id, p])), [positions]);

    const candidates = useMemo(() => (item ? branchEmployees(item) : []), [item]);

    // признак правок считается ниже, после раннего выхода, - сюда он приходит через ref
    const dirtyRef = useRef(false);
    useEffect(() => {
        onDirtyChange(dirtyRef.current);
    });
    useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

    if (!item || !unit) {
        dirtyRef.current = false;
        return null;
    }

    const type = types.find((t) => t.id === (unit.unitTypeId ?? ""));
    const staff = toStaff(staffRows, positionsById);
    const staffDirty = staff === null || !sameStaff(staff, unit.staff ?? []);
    const fieldsDirty =
        name.trim() !== unit.name ||
        order !== (unit.order != null ? String(unit.order) : "") ||
        typeId !== (unit.unitTypeId ?? "") ||
        managerId !== (unit.managerId ?? "") ||
        description.trim() !== (unit.description ?? "");
    const dirty = fieldsDirty || staffDirty;
    dirtyRef.current = dirty;
    const orderValue = order.trim() ? Number(order) : null;
    const orderInvalid = orderValue !== null && (!Number.isInteger(orderValue) || orderValue <= 0);
    const inside = describeUnit(item.stats.employees, item.stats.units);
    const hasContent = hasRealContent(item);

    const save = async () => {
        if (!name.trim() || orderInvalid || staff === null) {
            return;
        }
        setBusy(true);
        if (staffDirty) {
            await onSaveStaff(unit.id, staff);
        }
        if (!fieldsDirty) {
            setBusy(false);
            return;
        }
        await onSave({
            id: unit.id,
            name: name.trim(),
            description: description.trim(),
            order: orderValue,
            parentId: unit.parentId,
            unitTypeId: typeId || null,
            managerId: managerId || null,
        });
        setBusy(false);
    };

    const remove = async () => {
        setBusy(true);
        const ok = await onDelete(unit.id);
        setBusy(false);
        if (ok) {
            onClose();
        }
    };

    return (
        <motion.aside
            className={classNames(styles.panel, { [styles.wide]: edit })}
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 34 }}
            aria-label={`Подразделение ${unit.name}`}
        >
            <div className={styles.panelHead}>
                <div>
                    <div className={styles.panelType}>
                        <UnitTypeIcon name={type?.icon ?? "unit"} /> {type?.name ?? "Подразделение"}
                    </div>
                    <h3>{unit.name}</h3>
                </div>
                <button type="button" className={styles.close} onClick={onClose} aria-label="Закрыть">
                    <CloseIcon />
                </button>
            </div>

            <div className={edit ? styles.panelColumns : styles.panelBody}>
                <div className={edit ? styles.panelBody : styles.panelPlain}>
                    <div className={styles.stats}>
                        <div>
                            <b>{item.stats.employees}</b>
                            <span>сотрудников</span>
                        </div>
                        <div>
                            <b>{item.stats.units}</b>
                            <span>подразделений</span>
                        </div>
                        <div
                            title={`Свободных ставок во всей ветке; уровень в дереве — ${item.depth}, глубина ветки — ${unitHeight(item)}`}
                        >
                            <b>{item.stats.vacancies}</b>
                            <span>свободных ставок</span>
                        </div>
                    </div>

                    <Field label="Наименование">
                        <TextField value={name} disabled={!edit} onChange={setName} />
                    </Field>

                    <Field label="Тип узла">
                        <SelectField
                            value={typeId}
                            disabled={!edit}
                            emptyLabel="Подразделение"
                            options={types.map((t) => ({ id: t.id, label: t.name }))}
                            onChange={setTypeId}
                        />
                    </Field>

                    <Field label="Руководитель" hint="Из сотрудников этого подразделения и вложенных в него">
                        <SelectField
                            value={managerId}
                            disabled={!edit}
                            filterable
                            emptyLabel="Не назначен"
                            options={candidates.map((e) => ({
                                id: e.id,
                                label: e.positionName ? `${e.name} — ${e.positionName}` : e.name,
                            }))}
                            onChange={setManagerId}
                        />
                    </Field>

                    <Field label="Порядковый №" hint={orderInvalid ? "Номер должен быть больше 0" : undefined}>
                        <TextField value={order} disabled={!edit} onChange={(v) => setOrder(v.replace(/[^\d]/g, ""))} />
                    </Field>

                    <Field label="Примечания">
                        <AreaField value={description} disabled={!edit} onChange={setDescription} />
                    </Field>

                    {!edit && (
                        <Field label="Штат">
                            <StaffEditor
                                rows={staffRows}
                                positions={positions}
                                occupied={occupied}
                                edit={false}
                                onChange={setStaffRows}
                            />
                        </Field>
                    )}

                    {edit && confirmDelete && (
                        <div className={styles.notice}>
                            {hasContent
                                ? `Внутри ${inside || "есть сотрудники"}. Перенесите содержимое в другое место, затем удалите узел.`
                                : `Удалить «${unit.name}»? Действие можно отменить кнопкой «Отменить».`}
                        </div>
                    )}
                </div>

                {edit && (
                    // шапка «Штат / + Ставка» закреплена, прокручиваются только ставки
                    <div className={styles.staffColumn}>
                        <div className={styles.columnHead}>
                            <span className={styles.columnTitle}>Штат</span>
                            <PlanrButton
                                type="lightblueish"
                                size="small"
                                icon="general-plus-big"
                                disabled={staffRows.some((r) => !r.positionId) || staffRows.length >= positions.length}
                                onClick={addVacancy}
                            >
                                Ставка
                            </PlanrButton>
                        </div>
                        <div className={styles.panelBody} ref={staffScrollRef}>
                            <div className={styles.hint}>
                                Ставку занимает сотрудник подразделения с этой должностью, любого ранга. Свободные
                                ставки видны в дереве как вакансии.
                            </div>
                            <StaffEditor
                                rows={staffRows}
                                positions={positions}
                                occupied={occupied}
                                edit
                                onChange={setStaffRows}
                                onOpenPositions={canOpenPositions ? () => setDictionaryOpen(true) : undefined}
                            />
                            {staff === null && staffRows.some((r) => r.positionId) && (
                                <div className={styles.notice}>
                                    Укажите должность и число ставок (от 1 до 999) в каждой строке
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {edit && (
                <div className={styles.panelFoot}>
                    <PlanrButton
                        type="greenish"
                        disabled={!dirty || busy || !name.trim() || staff === null}
                        onClick={save}
                    >
                        Сохранить
                    </PlanrButton>
                    {!unit.system &&
                        (confirmDelete && !hasContent ? (
                            <PlanrButton type="danger" disabled={busy} onClick={remove}>
                                Да, удалить
                            </PlanrButton>
                        ) : (
                            <PlanrButton
                                type="lightdanger"
                                disabled={busy || (confirmDelete && hasContent)}
                                onClick={() => setConfirmDelete(true)}
                            >
                                Удалить
                            </PlanrButton>
                        ))}
                </div>
            )}
            {dictionaryOpen && (
                <PositionsDictionaryDialog
                    onClose={() => {
                        setDictionaryOpen(false);
                        onPositionsChanged();
                    }}
                />
            )}
        </motion.aside>
    );
};
